import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../../lib/api-client/transport";

export const supportAccessLost = (error: unknown) => error instanceof ApiError && [401, 403, 404].includes(error.status);
export const supportError = (error: unknown) => error instanceof Error ? error.message : "Couldn't complete the request. Retry.";

/** Refresh keeps authorized content and drafts; definitive refusal removes the content immediately.
 * Ignore superseded responses and work completed after navigation/profile unmount. */
export function useSupportRead<T>(read: () => Promise<T>) {
  const [data, updateData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const generation = useRef(0);
  const reload = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true); setError(null);
    try { const result = await read(); if (current === generation.current) updateData(result); }
    catch (failure) {
      if (current !== generation.current) return;
      setError(failure);
      if (supportAccessLost(failure)) updateData(null);
    } finally { if (current === generation.current) setLoading(false); }
  }, [read]);
  useEffect(() => { void reload(); return () => { generation.current++; }; }, [reload]);
  const setData = useCallback((result: T | null) => {
    // A write acknowledgement must win over a read started before that write.
    generation.current++; updateData(result); setLoading(false); setError(null);
  }, []);
  return { data, setData, error, loading, reload };
}
